import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import cn from 'classnames';

import type { IBrand } from './components/brand-list/models';
import type { TAlfaleasingPromotionsCatalog, IPromotion, ICategory } from './models';
import { useButtonMore, useCategories, usePromotions, setQuery, getQuery } from './utils';

const QUERY_CATEGORY_VALUE = 'category';
const NUMBER_INITIAL_CARDS = 6;
const NUMBER_ADDITIONAL_CARDS = 12;
const NUMBER_AUTOCOMPLETE_OPTIONS = 1000;

// TODO: логика компонента очень сложеная, имеет смысл его переписать
export const AlfaleasingPromotionsCatalog: FC<TAlfaleasingPromotionsCatalog> = props => {
    const {
        Autocomplete,
        BrandBar,
        BrandList,
        Button,
        Card,
        Heading,
        Layout,
        Tag,
        badgeImgDirectory,
        brands: allBrands,
        categories: allCategories,
        isBrandBarVisible,
        isBrandListVisible,
        isMobile,
        isSearchVisible,
        promotions: allPromotions,
        styles,
    } = props;

    const { defaultBrand, defaultCategory } = {
        defaultBrand: allBrands.find(({ isDefault }) => isDefault),
        defaultCategory: allCategories.find(({ isDefault }) => isDefault),
    };

    const categories: ICategory[] = useCategories(
        allPromotions,
        allBrands,
        allCategories,
        defaultBrand,
        defaultCategory
    );

    const [selectedCategoryValue, setSelectedCategoryValue] = useState(categories[0].value);

    // на момент внесение правок, на стенде инициализация списка НЕ происходит в момент первоначальной загрузки страницы,
    // (локально в сторибуке как будто бы сразу), поэтому сделал  инициализацию категории через useEffect
    useEffect(() => {
        const queryCategory = getQuery()[QUERY_CATEGORY_VALUE];
        setSelectedCategoryValue(
            categories.find(({ value }) => value === queryCategory)?.value || categories[0].value
        );
    }, [categories]);

    const {
        badges: selectedCategoryBadges,
        brands: selectedCategoryBrands,
        header: selectedCategoryHeader,
    } = categories.find(({ value }) => value === selectedCategoryValue) || categories[0];

    const [selectedBrandValue, setSelectedBrandValue] = useState(selectedCategoryBrands[0].value);

    const [searchValue, setSearchValue] = useState('');
    const foundBrandsValues = selectedCategoryBrands.reduce(
        (values: string[], { label }: IBrand) => {
            if (label === undefined || !label.toLowerCase().includes(searchValue.toLowerCase())) {
                return values;
            }

            return [...values, label];
        },
        []
    );

    const [displayedCardCount, setDisplayedCardCount] = useState(NUMBER_INITIAL_CARDS);
    const promotions = usePromotions(
        allPromotions,
        selectedCategoryValue,
        selectedBrandValue,
        defaultBrand,
        defaultCategory
    );
    const displayedPromotions = promotions.slice(0, displayedCardCount);

    const [isButtonMoreVisible, setIsButtonMoreVisible] = useButtonMore(
        displayedPromotions,
        promotions
    );

    const handleCategoryClick = (newCategoryValue: string) => {
        const newSelectedCategory = categories.find(({ value }) => value === newCategoryValue);
        const categoryDefaultBrand = selectedCategoryBrands.find(
            ({ value }: IBrand) => value === defaultBrand?.value
        );

        if (newSelectedCategory) {
            setSelectedCategoryValue(newSelectedCategory.value);
        }
        if (categoryDefaultBrand) {
            setSelectedBrandValue(categoryDefaultBrand.value);
        }
        setQuery({ [QUERY_CATEGORY_VALUE]: newCategoryValue });
        setSearchValue('');
        setDisplayedCardCount(NUMBER_INITIAL_CARDS);
    };

    const handleBrandClick = (newBrandValue: string) => {
        const newSelectedBrand = selectedCategoryBrands.find(
            ({ value }: IBrand) => value === newBrandValue
        );
        if (newSelectedBrand) {
            setSelectedBrandValue(newSelectedBrand.value);
        }
        setDisplayedCardCount(NUMBER_INITIAL_CARDS);
    };

    const handleMoreClick = () => {
        setIsButtonMoreVisible(false);
        setDisplayedCardCount(prevCount => prevCount + NUMBER_ADDITIONAL_CARDS);
    };

    const handleSearchChange = (value: string) => {
        const brands = selectedCategoryBrands.filter(({ label }: IBrand) =>
            label ? value.toLowerCase() === label.toLowerCase() : false
        );

        if (brands.length === 1) {
            setSelectedBrandValue(brands[0].value);
        } else if (value.length === 0 && defaultBrand) {
            setSelectedBrandValue(defaultBrand.value);
        }

        setSearchValue(value);
    };

    return (
        <div>
            <Heading
                className={cn(styles.catalogTitle, styles.external)}
                marginBottom={null}
                marginTop={false}
                priority={1}
                size={2}
            >
                {selectedCategoryHeader}
            </Heading>
            <Layout hasPadding={false} scroll={isMobile}>
                <div className={styles.categories}>
                    {categories.map(({ label, value }) => (
                        <Tag
                            key={value}
                            className={styles.label}
                            label={label}
                            onClick={handleCategoryClick}
                            selected={selectedCategoryValue}
                            size="s"
                            value={value}
                        />
                    ))}
                </div>
            </Layout>
            {isBrandBarVisible && selectedCategoryBadges && (
                <BrandBar
                    badgeImgDirectory={badgeImgDirectory}
                    badges={selectedCategoryBadges}
                    onClick={handleBrandClick}
                />
            )}
            {isBrandListVisible && (
                <BrandList
                    brands={selectedCategoryBrands}
                    columnsNumber={6}
                    onClick={(value: string) => handleBrandClick(value)}
                    selectedBrand={selectedBrandValue}
                />
            )}
            {isSearchVisible && (
                <section className={styles.searchSection}>
                    <Autocomplete
                        list={foundBrandsValues}
                        listId="brands"
                        maxOption={NUMBER_AUTOCOMPLETE_OPTIONS}
                        onChange={({ value }) => handleSearchChange(value as string)}
                        placeholder="Марка"
                        type="text"
                        value={searchValue}
                    />
                </section>
            )}
            <section className={styles.promotionsSection}>
                <Heading
                    className={cn(styles.promotionsTitle, styles.external)}
                    marginBottom={null}
                    marginTop={false}
                    priority={3}
                    size={3}
                >
                    {
                        selectedCategoryBrands.find(({ value }) => value === selectedBrandValue)
                            ?.label
                    }
                </Heading>
                <div className={styles.promotions}>
                    {displayedPromotions.map((promotion: IPromotion, indx: number) => (
                        <div
                            key={`${promotion.title}_${indx}`}
                            className={styles.promotionContainer}
                        >
                            <Card {...promotion} interactiveHover={!isMobile} />
                        </div>
                    ))}
                </div>
                {isButtonMoreVisible && (
                    <Button
                        className={cn(styles.buttonShowMore, styles.external)}
                        kind="translucent"
                        onClick={handleMoreClick}
                    >
                        Показать ещё
                    </Button>
                )}
            </section>
        </div>
    );
};
